import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  faCheck,
  faChevronDown,
  faCircleNotch,
  faExclamationCircle,
  faExpand,
  faPause,
  faStop,
  faWarning,
} from "@fortawesome/pro-regular-svg-icons";
import * as RadixAccordion from "@radix-ui/react-accordion";
import { useKeyDown } from "@react-hooks-library/core";
import { AnimatePresence, m } from "framer-motion";
import isEqual from "lodash/isEqual";
import { Control, Controller, useForm } from "react-hook-form";
import Markdown from "react-markdown";
import { twJoin, twMerge } from "tailwind-merge";
import { v4 as uuidv4 } from "uuid";

import {
  AgentSchemaPropertyUI,
  convertAgentSchemaBEtoUI,
} from "src/agents/AgentVersionPage/AgentSchemaEditor";
import {
  ActiveSubstatus,
  PlaygroundSessionState,
  StoppedSubstatus,
  usePlaygroundContext,
} from "src/agents/AgentVersionPage/PlaygroundContext";
import { PlaygroundFileField } from "src/agents/AgentVersionPage/PlaygroundFileField";
import { PlaygroundFileListField } from "src/agents/AgentVersionPage/PlaygroundFileListField";
import { TOOL_ICON_CONFIG } from "src/agents/base/ToolCard";
import { DurationPill, TokensPill } from "src/agents/MetricPills";
import { useAgentVersion } from "src/agents/queries/management";
import { Schema } from "src/api/flowTypes";
import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { DateTimePickerDropdown } from "src/base-components/DateTimePickerDropdown";
import { Divider } from "src/base-components/Divider";
import { ErrorHint } from "src/base-components/ErrorHint";
import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/base-components/FloatingWindow/FloatingWindow";
import { useFloatingWindowsActions } from "src/base-components/FloatingWindow/hooks";
import { FloatingWindowProps } from "src/base-components/FloatingWindow/store";
import { FormItem } from "src/base-components/FormItem";
import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { Pill } from "src/base-components/Pill";
import { Select } from "src/base-components/Select";
import { Switch } from "src/base-components/Switch";
import { Tabs } from "src/base-components/Tabs";
import { Textarea } from "src/base-components/Textarea";
import type {
  SessionResponseMessagesInner,
  SessionResultResponse,
  TerminationReason,
  ToolCallContentBlock,
  ToolMessage,
  ToolType,
} from "src/clients/agent-management";
import { Callout } from "src/design-system/Callout";
import { EmptyState } from "src/design-system/EmptyState";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { EnumValueObject } from "src/schema/schemaMappingUtils";
import { ErrorBoundary } from "src/utils/ErrorBoundary";

export type AgentPlaygroundFormValues = {
  structuredInput: Record<string, unknown>;
  instructions: string;
};

type AgentPlaygroundContentProps = {
  agentId: string;
  versionId: string;
};

const generateDefaultValues = (
  properties: AgentSchemaPropertyUI[],
): AgentPlaygroundFormValues => {
  const structuredInput: Record<string, unknown> = {};
  for (const prop of properties) {
    const type = prop.type?.[0] ?? "string";
    switch (type) {
      case "string":
        structuredInput[prop.fieldName] = "";
        break;
      case "number":
      case "integer":
        structuredInput[prop.fieldName] = undefined;
        break;
      case "boolean":
        structuredInput[prop.fieldName] = false;
        break;
      case "enum":
        structuredInput[prop.fieldName] = undefined;
        break;
      case "date":
      case "datetime":
        structuredInput[prop.fieldName] = undefined;
        break;
      case "array":
        structuredInput[prop.fieldName] = "[]";
        break;
      case "object":
        structuredInput[prop.fieldName] = "{}";
        break;
      case "file":
        structuredInput[prop.fieldName] = undefined;
        break;
      case "file[]":
        structuredInput[prop.fieldName] = [];
        break;
    }
  }
  return {
    structuredInput,
    instructions: "",
  };
};

/**
 * Parses JSON string values in structuredInput back to arrays/objects.
 * CodeEditor stores array/object fields as strings; this converts them
 * before JSON.stringify to avoid double-encoding.
 */
const parseJsonFieldsInStructuredInput = (
  values: AgentPlaygroundFormValues,
  properties: AgentSchemaPropertyUI[],
): AgentPlaygroundFormValues => {
  const parsedStructuredInput: Record<string, unknown> = {};

  // Build a set of field names that are array or object type
  const objectAndArrayFieldNames = new Set(
    properties
      .filter((p) => {
        const type = p.type?.[0];
        return type === "array" || type === "object";
      })
      .map((p) => p.fieldName),
  );

  for (const [key, value] of Object.entries(values.structuredInput)) {
    if (typeof value === "string" && objectAndArrayFieldNames.has(key)) {
      // Handle empty strings for optional fields - treat as undefined
      if (!value.trim()) {
        parsedStructuredInput[key] = undefined;
      } else {
        parsedStructuredInput[key] = JSON.parse(value);
      }
    } else {
      parsedStructuredInput[key] = value;
    }
  }

  return {
    ...values,
    structuredInput: parsedStructuredInput,
  };
};

export const AgentPlaygroundContent: React.FC<AgentPlaygroundContentProps> = ({
  agentId,
  versionId,
}) => {
  const [isInputAccordionExpanded, setIsInputAccordionExpanded] =
    useState(true);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const versionQuery = useAgentVersion(agentId, versionId);
  // Get session state and invoke from playground context
  const {
    sessionState,
    rawSessionData,
    invoke: invokeAgent,
    registerStartTest,
  } = usePlaygroundContext();

  const inputSchema = versionQuery.data?.configuration.input_schema;

  const properties = useMemo(
    () =>
      convertAgentSchemaBEtoUI(inputSchema as Schema | null)?.properties ?? [],
    [inputSchema],
  );

  const playgroundForm = useForm<AgentPlaygroundFormValues>({
    defaultValues: generateDefaultValues(properties),
    mode: "onChange",
  });

  const prevPropertiesRef = useRef<AgentSchemaPropertyUI[]>(properties);

  // Reset form when input schema changes, preserving values for unchanged fields
  useEffect(() => {
    const prevProperties = prevPropertiesRef.current;
    const currentValues = playgroundForm.getValues();
    const newDefaults = generateDefaultValues(properties);
    const currentStructuredInput = currentValues.structuredInput;
    const newStructuredInput = newDefaults.structuredInput;

    // Merge: keep current value if field exists with same type (and still valid for enums), else use default
    const mergedStructuredInput: Record<string, unknown> = {};
    for (const prop of properties) {
      const prevProp = prevProperties.find(
        (p) => p.fieldName === prop.fieldName,
      );
      const propType = prop.type?.[0] ?? "string";
      const isSameType = prevProp?.type?.[0] === propType;
      const currentValue = currentStructuredInput[prop.fieldName];
      const enumValues = (prop.enum as EnumValueObject[] | undefined)?.map(
        (e) => e.value,
      );
      const isValidEnumValue =
        propType !== "enum" ||
        (enumValues ?? []).includes(currentValue as string);

      if (
        isSameType &&
        isValidEnumValue &&
        prop.fieldName in currentStructuredInput
      ) {
        mergedStructuredInput[prop.fieldName] = currentValue;
      } else {
        mergedStructuredInput[prop.fieldName] =
          newStructuredInput[prop.fieldName];
      }
    }

    playgroundForm.reset({
      structuredInput: mergedStructuredInput,
      instructions: currentValues.instructions,
    });
    setHasAttemptedSubmit(false);
    prevPropertiesRef.current = properties;
  }, [properties, playgroundForm]);

  // Expand accordion when session becomes inactive (stopped, completed, failed)
  const prevIsActiveRef = useRef(sessionState.status === "active");
  useEffect(() => {
    if (prevIsActiveRef.current && sessionState.status !== "active") {
      // Was active, now not active (stopped or completed)
      setIsInputAccordionExpanded(true);
    }
    prevIsActiveRef.current = sessionState.status === "active";
  }, [sessionState.status]);

  const handleSubmit = useCallback(
    (values: AgentPlaygroundFormValues) => {
      // Parse array/object fields that were stored as JSON strings in CodeEditor
      const parsedValues = parseJsonFieldsInStructuredInput(values, properties);

      // Separate instructions from structured input
      const invocationMessage = values.instructions;
      const structuredInput = parsedValues.structuredInput;

      // Auto-collapse the input section when test starts
      setIsInputAccordionExpanded(false);

      invokeAgent.mutate(
        {
          invocationMessage,
          structuredInput,
          inputSchema: (inputSchema as Record<string, unknown>) ?? null,
          submittedInput: values,
        },
        {
          onError: () => {
            // Context clears session on error, just show toast and expand accordion
            setIsInputAccordionExpanded(true);
            toastActions.failure({
              title: "Failed to run agent",
              description: "Please try again",
            });
          },
        },
      );
    },
    [invokeAgent, properties, inputSchema],
  );

  const handleStartTest = useCallback(() => {
    setHasAttemptedSubmit(true);
    playgroundForm.handleSubmit(
      (values) => {
        handleSubmit(values);
      },
      () => {
        setIsInputAccordionExpanded(true);
      },
    )();
  }, [playgroundForm, handleSubmit]);

  // Register handleStartTest with context so header button can trigger it
  useEffect(() => {
    registerStartTest(handleStartTest);
  }, [registerStartTest, handleStartTest]);

  // Cmd/Ctrl + Enter triggers start test when a playground input is focused
  const playgroundContainerRef = useRef<HTMLDivElement>(null);
  useKeyDown(
    ["Enter"],
    (e) => {
      if ((e.metaKey || e.ctrlKey) && sessionState.status !== "active") {
        e.preventDefault();
        handleStartTest();
      }
    },
    { target: playgroundContainerRef },
  );

  return (
    <div ref={playgroundContainerRef} className="flex h-full w-full flex-col">
      <ErrorBoundary
        fallbackErrorContent={
          <div className="flex flex-1 items-center justify-center p-5">
            <EmptyState
              description="Something went wrong. Please close and reopen the panel to try again."
              headline="Playground error"
              icon={faExclamationCircle}
              variant="error"
            />
          </div>
        }
      >
        <div
          className={twJoin(
            "decideScrollbar compensate-scrollbar-gutter flex h-full w-full flex-1 flex-col pr-1 pl-5",
            sessionState.status === "idle"
              ? "overflow-y-auto pb-5"
              : "overflow-hidden",
          )}
        >
          {/* Config updated after test started banner */}
          {sessionState.status !== "idle" &&
            rawSessionData?.agent_version_etag != null &&
            versionQuery.data?.etag != null &&
            rawSessionData.agent_version_etag !== versionQuery.data.etag && (
              <div className="mt-4 flex-shrink-0">
                <Callout type="info">
                  <span className="font-inter-medium-12px">
                    The config has been updated since this test started. Run
                    again to test with the latest changes.
                  </span>
                </Callout>
              </div>
            )}

          {/* Success banner */}
          {sessionState.status === "stopped" &&
            sessionState.substatus === "success" && (
              <div className="mt-4 flex-shrink-0">
                <Callout type="success">
                  <span className="font-inter-medium-12px">
                    Agent test run was successful
                  </span>
                </Callout>
              </div>
            )}

          {/* Failed banner */}
          {sessionState.status === "stopped" &&
            sessionState.substatus === "failed" && (
              <div className="mt-4 flex-shrink-0">
                <Callout type="error">
                  <div className="flex flex-col">
                    <span className="font-inter-medium-12px">
                      {sessionState.terminationReason
                        ? `Agent failed: ${formatTerminationReason(sessionState.terminationReason)}`
                        : "Agent failed"}
                    </span>
                    {sessionState.terminationMessage && (
                      <span className="font-inter-normal-12px">
                        {sessionState.terminationMessage}
                      </span>
                    )}
                  </div>
                </Callout>
              </div>
            )}

          {/* Cancelled banner */}
          {sessionState.status === "stopped" &&
            sessionState.substatus === "cancelled" && (
              <div className="mt-4 flex-shrink-0">
                <Callout type="neutral">
                  <span className="font-inter-medium-12px">
                    Session stopped
                  </span>
                </Callout>
              </div>
            )}

          {/* Collapsible Test Input Section — hidden while active; readonly copy scrolls with messages */}
          {sessionState.status !== "active" && (
            <div className="mt-4 flex-shrink-0">
              <TestInputAccordion
                control={playgroundForm.control}
                dataLoc="test-input-accordion"
                disabled={false}
                expanded={isInputAccordionExpanded}
                properties={properties}
                showErrors={hasAttemptedSubmit}
                onExpandedChange={setIsInputAccordionExpanded}
                defaultOpen
              />
            </div>
          )}

          {/* Results Section - shown when there's a session */}
          {sessionState.status !== "idle" && (
            <ExecutionLogView
              key={sessionState.sessionId}
              hideTestInput={sessionState.status !== "active"}
              sessionState={sessionState}
            />
          )}
        </div>
      </ErrorBoundary>
    </div>
  );
};

type ToolCallWithResult = {
  toolCall: ToolCallContentBlock;
  toolResult: ToolMessage | null;
};

type ProcessedDisplayItem =
  | { kind: "ai-text"; id: string; content: string }
  | {
      kind: "tool-group";
      id: string;
      toolCallsWithResults: ToolCallWithResult[];
    };

const useProcessedMessages = (
  sessionMessages: SessionResponseMessagesInner[],
  result: SessionResultResponse | null,
): ProcessedDisplayItem[] => {
  return useMemo(() => {
    // Build lookup: tool_call_id → ToolMessage
    const toolResultMap = new Map<string, ToolMessage>();
    for (const msg of sessionMessages) {
      if (msg.type === "tool") {
        toolResultMap.set(msg.tool_call_id, msg);
      }
    }

    const items: ProcessedDisplayItem[] = [];

    for (const msg of sessionMessages) {
      if (msg.type !== "ai") continue;

      const blocks = msg.content_blocks ?? [];

      for (const [i, block] of blocks.entries()) {
        if (block.type === "text" && block.text) {
          items.push({
            kind: "ai-text",
            id: `${msg.id}-${i}`,
            content: block.text,
          });
        }
      }

      const toolCalls = blocks.filter(
        (b) => b.type === "tool_call" && b.metadata.type in TOOL_ICON_CONFIG,
      ) as ToolCallContentBlock[];

      if (toolCalls.length > 0) {
        items.push({
          kind: "tool-group",
          id: `${msg.id}-tools`,
          toolCallsWithResults: toolCalls.map((tc) => ({
            toolCall: tc,
            toolResult: toolResultMap.get(tc.id) ?? null,
          })),
        });
      }
    }

    // Deduplicate: hide last AI text if it matches the agent output
    if (result && items.length > 0) {
      const lastItem = items[items.length - 1];
      if (lastItem.kind === "ai-text") {
        const trimmed = lastItem.content.trim();
        const matchesText =
          result.text != null && trimmed === result.text.trim();
        let matchesStructured = false;
        if (result.structured_output != null) {
          try {
            const parsed = JSON.parse(trimmed);
            matchesStructured = isEqual(parsed, result.structured_output);
          } catch {
            // Not valid JSON — no match
          }
        }
        if (matchesText || matchesStructured) {
          items.pop();
        }
      }
    }

    return items;
  }, [sessionMessages, result]);
};

export type ExecutionLogViewProps = {
  hideTestInput: boolean;
  sessionState: Exclude<PlaygroundSessionState, { status: "idle" }>;
  inputTitle?: string;
};

export const ExecutionLogView: React.FC<ExecutionLogViewProps> = ({
  hideTestInput,
  sessionState,
  inputTitle,
}) => {
  const { sessionMessages, inputSchema, submittedInput } = sessionState;

  // Auto-scroll logic for the internal scroll container
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    isAtBottomRef.current = scrollHeight - scrollTop - clientHeight < 50;
  }, []);

  // Auto-scroll when new messages arrive (only if user was at bottom)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    if (isAtBottomRef.current) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [sessionMessages.length]);

  const readonlyProperties = useMemo(
    () =>
      convertAgentSchemaBEtoUI(inputSchema as Schema | null)?.properties ?? [],
    [inputSchema],
  );

  const readonlyForm = useForm<AgentPlaygroundFormValues>({
    defaultValues: submittedInput ?? { structuredInput: {}, instructions: "" },
  });

  // Result for the agent output accordion (success only)
  const result =
    sessionState.status === "stopped" && sessionState.substatus === "success"
      ? sessionState.result
      : null;

  // Process messages into structured display items
  const processedItems = useProcessedMessages(sessionMessages, result);

  // Determine session substatus for tool call status computation
  const activeSubstatus: ActiveSubstatus | null =
    sessionState.status === "active" ? sessionState.substatus : null;

  // Count tool calls awaiting results (paused tools)
  const pausedToolCount = useMemo(() => {
    if (activeSubstatus !== "paused") return 0;
    return processedItems.reduce((count, item) => {
      if (item.kind !== "tool-group") return count;
      return (
        count + item.toolCallsWithResults.filter((t) => !t.toolResult).length
      );
    }, 0);
  }, [processedItems, activeSubstatus]);

  // Show status display only for active states (all stopped states use top banners)
  const showStatusDisplay = sessionState.status === "active";

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div
        ref={scrollContainerRef}
        className="decideScrollbar flex-1 overflow-y-auto pb-5"
        onScroll={handleScroll}
      >
        {/* Read-only test input accordion showing frozen values */}
        {!hideTestInput && (
          <div className="mt-4">
            <TestInputAccordion
              control={readonlyForm.control}
              dataLoc="readonly-test-input-accordion"
              properties={readonlyProperties}
              title={inputTitle}
              disabled
              defaultOpen={false}
            />
          </div>
        )}
        <div className="space-y-3 py-4">
          {/* Agent messages */}
          {processedItems.map((item) => (
            <m.div
              key={item.id}
              animate={{ opacity: 1 }}
              initial={{ opacity: 0 }}
              transition={{ duration: 0.15, delay: 0.1, ease: "easeOut" }}
            >
              {item.kind === "ai-text" ? (
                <div className="tktl-markdown font-inter-normal-12px text-gray-700">
                  <Markdown>{item.content}</Markdown>
                </div>
              ) : (
                <ToolCallGroup item={item} sessionSubstatus={activeSubstatus} />
              )}
            </m.div>
          ))}

          {/* Session status display (thinking, paused) */}
          {showStatusDisplay && (
            <SessionStatusDisplay
              pausedToolCount={pausedToolCount}
              sessionStatus={sessionState}
            />
          )}

          {/* Agent output accordion (shown on success) */}
          {result && (
            <m.div
              animate={{ opacity: 1 }}
              initial={{ opacity: 0 }}
              transition={{ duration: 0.15, delay: 0.1, ease: "easeOut" }}
            >
              <AccordionDisclosure
                rightSlot={<AgentOutputStatusPill substatus="success" />}
                title="Agent output"
                defaultOpen
              >
                <AgentOutputAccordionBody result={result} />
              </AccordionDisclosure>
            </m.div>
          )}
        </div>
      </div>
    </div>
  );
};

type TestInputAccordionProps = {
  properties: AgentSchemaPropertyUI[];
  control: Control<AgentPlaygroundFormValues>;
  disabled: boolean;
  expanded?: boolean;
  onExpandedChange?: (v: boolean) => void;
  dataLoc: string;
  title?: string;
  showErrors?: boolean;
  defaultOpen: boolean;
};

const TestInputAccordion: React.FC<TestInputAccordionProps> = ({
  properties,
  control,
  disabled,
  expanded,
  onExpandedChange,
  dataLoc,
  title = "Test input",
  showErrors = false,
  defaultOpen,
}) => {
  return (
    <AccordionDisclosure
      dataLoc={dataLoc}
      open={expanded}
      title={title}
      defaultOpen={defaultOpen}
      onOpenChange={onExpandedChange}
    >
      <div className="space-y-4 px-3 pb-3">
        {properties.length > 0 && (
          <div className="rounded-lg bg-gray-50 px-4 py-3">
            {properties.map((property) => (
              <FormItem key={property.fieldName} gap="xs">
                <FormItem.Label isRequired={property.required}>
                  {property.fieldName}
                </FormItem.Label>
                <PlaygroundInputField
                  control={control}
                  disabled={disabled}
                  fieldPath={`structuredInput.${property.fieldName}`}
                  property={property}
                  showErrors={showErrors}
                />
              </FormItem>
            ))}
          </div>
        )}
        <div className="rounded-lg bg-gray-50 px-4 py-3">
          <FormItem gap="xs">
            <FormItem.Label>Agent instructions (optional)</FormItem.Label>
            <Controller
              control={control}
              name="instructions"
              render={({ field }) => (
                <Textarea
                  data-loc="test-input-instructions"
                  disabled={disabled}
                  placeholder="Write test instructions for your Agent"
                  value={field.value}
                  fullWidth
                  onChange={field.onChange}
                />
              )}
            />
          </FormItem>
        </div>
      </div>
    </AccordionDisclosure>
  );
};

type PlaygroundInputFieldProps = {
  property: AgentSchemaPropertyUI;
  control: Control<AgentPlaygroundFormValues>;
  fieldPath: `structuredInput.${string}`;
  disabled?: boolean;
  showErrors?: boolean;
};

const PlaygroundInputField: React.FC<PlaygroundInputFieldProps> = ({
  property,
  control,
  fieldPath,
  disabled = false,
  showErrors = false,
}) => {
  const { fieldName, required } = property;
  const type = property.type?.[0] ?? "string";
  const enumValues = (property.enum as EnumValueObject[] | undefined)?.map(
    (e) => e.value,
  );

  switch (type) {
    case "string":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <Input
                data-loc={`test-input-${fieldName}`}
                disabled={disabled}
                placeholder={fieldName}
                value={(field.value as string) ?? ""}
                fullWidth
                onChange={field.onChange}
              />
              {showErrors && fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: required
              ? (value) =>
                  (value as string)?.trim() !== "" || "Field is required"
              : undefined,
          }}
        />
      );

    case "number":
    case "integer":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <Input
                data-loc={`test-input-${fieldName}`}
                disabled={disabled}
                placeholder={fieldName}
                type="number"
                value={(field.value as string) ?? ""}
                fullWidth
                onChange={(e) => {
                  const val = e.target.value;
                  field.onChange(val === "" ? null : Number(val));
                }}
              />
              {showErrors && fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: required
              ? (value) => value != null || "Field is required"
              : undefined,
          }}
        />
      );

    case "boolean":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field }) => (
            <div>
              <Switch
                dataLoc={`test-input-${fieldName}`}
                disabled={disabled}
                enabled={(field.value as boolean) ?? false}
                onChange={field.onChange}
              />
            </div>
          )}
        />
      );

    case "date":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <DateTimePickerDropdown
                disabled={disabled}
                placeholder={`Select ${fieldName}`}
                value={field.value as Date | undefined}
                allowFutureDates
                dateOnly
                onChange={field.onChange}
                onReset={() => field.onChange(null)}
              />
              {showErrors && fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: required
              ? (value) => value != null || "Field is required"
              : undefined,
          }}
        />
      );

    case "datetime":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <DateTimePickerDropdown
                disabled={disabled}
                placeholder={`Select ${fieldName}`}
                value={field.value as Date | undefined}
                allowFutureDates
                onChange={field.onChange}
                onReset={() => field.onChange(null)}
              />
              {showErrors && fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: required
              ? (value) => value != null || "Field is required"
              : undefined,
          }}
        />
      );

    case "enum":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <Select
                dataLoc={`test-input-${fieldName}`}
                disabled={disabled}
                options={(enumValues ?? []).map((val) => ({
                  key: val,
                  value: val,
                }))}
                placeholder={`Select ${fieldName}`}
                value={(field.value as string) ?? ""}
                onChange={field.onChange}
              />
              {showErrors && fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: required
              ? (value) => value != null || "Field is required"
              : undefined,
          }}
        />
      );

    case "array":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <div className="h-32">
                <CodeEditor
                  backgroundColor="white"
                  dataLoc={`test-input-${fieldName}`}
                  disabled={disabled}
                  language="json"
                  value={(field.value as string) ?? "[]"}
                  lineWrapping
                  onChange={(value) => {
                    field.onChange(value ?? "[]");
                  }}
                />
              </div>
              {showErrors && fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: (value) => {
              if (!value) {
                return !required || "Field is required";
              }
              try {
                const parsed = JSON.parse(value as string);
                if (!Array.isArray(parsed)) {
                  return "Field is not an array";
                }
              } catch {
                return "Field is invalid JSON";
              }
            },
          }}
        />
      );

    case "object":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <div className="h-32">
                <CodeEditor
                  backgroundColor="white"
                  dataLoc={`test-input-${fieldName}`}
                  disabled={disabled}
                  language="json"
                  value={(field.value as string) ?? "{}"}
                  lineWrapping
                  onChange={(value) => {
                    field.onChange(value ?? "{}");
                  }}
                />
              </div>
              {showErrors && fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: (value) => {
              if (!value) {
                return !required || "Field is required";
              }
              try {
                const parsed = JSON.parse(value as string);
                if (
                  typeof parsed !== "object" ||
                  parsed === null ||
                  Array.isArray(parsed)
                ) {
                  return "Field is not an object";
                }
                return true;
              } catch {
                return "Field is invalid JSON";
              }
            },
          }}
        />
      );

    case "file":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <PlaygroundFileField
                disabled={disabled}
                field={field}
                fieldName={fieldName}
              />
              {showErrors && fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: required
              ? (value) => value !== undefined || "Field is required"
              : undefined,
          }}
        />
      );

    case "file[]":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <PlaygroundFileListField
                disabled={disabled}
                field={field}
                fieldName={fieldName}
              />
              {showErrors && fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: required
              ? (value) =>
                  (Array.isArray(value) && value.length > 0) ||
                  "At least one file is required"
              : undefined,
          }}
        />
      );

    default:
      return null;
  }
};

type ToolCallStatus = "running" | "paused" | "success" | "error";

const computeToolCallStatus = (
  toolResult: ToolMessage | null,
  sessionSubstatus: ActiveSubstatus | null,
): ToolCallStatus | null => {
  if (toolResult) {
    return toolResult.status === "error" ? "error" : "success";
  }
  if (sessionSubstatus === "paused") return "paused";
  if (sessionSubstatus) return "running";
  return null;
};

const ToolCallGroup: React.FC<{
  item: Extract<ProcessedDisplayItem, { kind: "tool-group" }>;
  sessionSubstatus: ActiveSubstatus | null;
}> = ({ item, sessionSubstatus }) => {
  const isParallel = item.toolCallsWithResults.length > 1;

  const toolCallItems = item.toolCallsWithResults.map((tcwr) => (
    <ToolCallAccordionItem
      key={tcwr.toolCall.id}
      status={computeToolCallStatus(tcwr.toolResult, sessionSubstatus)}
      toolCall={tcwr.toolCall}
      toolResult={tcwr.toolResult}
    />
  ));

  return (
    <div className="space-y-2">
      {isParallel ? (
        <ParallelExecutionWrapper count={item.toolCallsWithResults.length}>
          {toolCallItems}
        </ParallelExecutionWrapper>
      ) : (
        toolCallItems
      )}
    </div>
  );
};

const formatTerminationReason = (reason: TerminationReason): string => {
  const reasonMap: Record<TerminationReason, string> = {
    expired: "Session expired",
    invocation_runtime_limit: "Runtime limit exceeded",
    model_call_limit: "Model call limit exceeded",
    tool_call_limit: "Tool call limit exceeded",
    infinite_tool_loop: "Infinite tool loop detected",
    error: "Internal error",
    user_cancelled: "Cancelled by user",
    upstream_cancelled: "Cancelled upstream",
    version_diverged: "Version diverged",
    quota_exhausted: "Monthly token quota/cost limit exhausted",
    rate_limit_exceeded: "Request/token rate limit exceeded",
    redrive_limit_exceeded: "Redrive limit exceeded",
    llm_provider_bad_request: "LLM provider bad request",
    prompt_injection: "Prompt injection detected",
    tool_execution_error: "Tool execution error",
  };
  return reasonMap[reason] ?? reason;
};

type SessionStatusDisplayProps = {
  sessionStatus: PlaygroundSessionState & { status: "active" };
  pausedToolCount: number;
};

const SessionStatusDisplay: React.FC<SessionStatusDisplayProps> = ({
  sessionStatus,
  pausedToolCount,
}) => {
  const { substatus } = sessionStatus;

  // Booting/Running: indigo spinner with rotating text
  if (substatus === "booting" || substatus === "running") {
    return <ThinkingStatusIndicator substatus={substatus} />;
  }

  // Paused
  if (substatus === "paused") {
    const waitingText =
      pausedToolCount > 1
        ? "Waiting for the tool responses to proceed."
        : "Waiting for the tool response to proceed.";
    return (
      <StatusIndicator
        icon={
          <Icon
            color="text-gray-600"
            icon={faPause}
            padding={false}
            size="xs"
          />
        }
        statusText={`Agent paused. ${waitingText}`}
      />
    );
  }

  return null;
};

const ParallelExecutionWrapper: React.FC<{
  count: number;
  children: React.ReactNode;
}> = ({ count, children }) => {
  return (
    <div>
      <div className="flex items-center gap-2 pb-2">
        <span className="font-inter-normal-12px text-gray-500">
          Parallel execution ({count} tools)
        </span>
      </div>
      <div className="space-y-4 border-gray-200 border-l pl-3">{children}</div>
    </div>
  );
};

const AccordionDisclosure: React.FC<{
  title: React.ReactNode;
  icon?: React.ReactNode;
  rightSlot?: React.ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
  erroredBorder?: boolean;
  dataLoc?: string;
}> = ({
  title,
  icon,
  rightSlot,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  children,
  erroredBorder = false,
  dataLoc,
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const DISCLOSURE_ITEM_VALUE = "content";
  const rootProps = isControlled
    ? {
        value: controlledOpen ? [DISCLOSURE_ITEM_VALUE] : ([] as string[]),
        onValueChange: (values: string[]) =>
          onOpenChange?.(values.includes(DISCLOSURE_ITEM_VALUE)),
      }
    : {
        defaultValue: defaultOpen ? [DISCLOSURE_ITEM_VALUE] : ([] as string[]),
        onValueChange: (values: string[]) =>
          setInternalOpen(values.includes(DISCLOSURE_ITEM_VALUE)),
      };

  return (
    <RadixAccordion.Root type="multiple" {...rootProps}>
      <RadixAccordion.Item value={DISCLOSURE_ITEM_VALUE} asChild>
        <div
          className={twMerge(
            "rounded-lg border border-gray-200 bg-white",
            erroredBorder && "border-red-500",
          )}
          data-loc={dataLoc}
        >
          <RadixAccordion.Header asChild>
            <RadixAccordion.Trigger className="flex w-full items-center gap-2 p-3">
              <div
                className={twJoin(
                  "flex h-4 w-4 shrink-0 items-center justify-center transition-transform duration-200",
                  isOpen ? "rotate-0" : "-rotate-90",
                )}
              >
                <Icon icon={faChevronDown} size="2xs" />
              </div>
              {icon}
              <span className="min-w-0 flex-1 truncate text-left font-inter-medium-12px text-gray-800">
                {title}
              </span>
              {rightSlot}
            </RadixAccordion.Trigger>
          </RadixAccordion.Header>
          <RadixAccordion.Content asChild forceMount>
            <m.div
              animate={
                isOpen
                  ? { height: "auto", opacity: 1 }
                  : { height: 0, opacity: 0 }
              }
              initial={false}
              style={{ overflow: "hidden" }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              {children}
            </m.div>
          </RadixAccordion.Content>
        </div>
      </RadixAccordion.Item>
    </RadixAccordion.Root>
  );
};

const ToolCallAccordionItem: React.FC<{
  toolCall: ToolCallContentBlock;
  toolResult: ToolMessage | null;
  status: ToolCallStatus | null;
}> = ({ toolCall, toolResult, status }) => {
  const durationMs = toolResult
    ? new Date(toolResult.finished_at).getTime() -
      new Date(toolResult.started_at).getTime()
    : null;

  return (
    <AccordionDisclosure
      erroredBorder={status === "error"}
      icon={<ToolTypeIcon type={toolCall.metadata.type} />}
      rightSlot={
        <div className="flex items-center gap-1.5">
          {toolResult && durationMs != null && (
            <Tooltip placement="top" title="Execution duration">
              <DurationPill durationMs={durationMs} />
            </Tooltip>
          )}
          {toolResult && (
            <Tooltip placement="top" title="Token usage">
              <TokensPill count={toolResult.token_count} />
            </Tooltip>
          )}
          {status && <ToolCallStatusPill status={status} />}
        </div>
      }
      title={toolCall.metadata.name}
    >
      <ToolCallBody toolCall={toolCall} toolResult={toolResult} />
    </AccordionDisclosure>
  );
};

const THINKING_VERBS = [
  "Thinking...",
  "Cogitating...",
  "Working...",
  "Wondering...",
  "Reflecting...",
  "Musing...",
  "Pondering...",
];
const VERB_ROTATION_INTERVAL_MS = 4500;

type ThinkingStatusIndicatorProps = {
  substatus: ActiveSubstatus;
};

const ThinkingStatusIndicator: React.FC<ThinkingStatusIndicatorProps> = ({
  substatus,
}) => {
  const [verbIndex, setVerbIndex] = useState(0);
  const isBooting = substatus === "booting";

  useEffect(() => {
    if (isBooting) {
      setVerbIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setVerbIndex((prev) => (prev + 1) % THINKING_VERBS.length);
    }, VERB_ROTATION_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isBooting]);

  const statusText = isBooting ? "Booting..." : THINKING_VERBS[verbIndex];

  return (
    <StatusIndicator
      icon={
        <Icon
          color="text-indigo-500"
          icon={faCircleNotch}
          padding={false}
          size="xs"
          spin
        />
      }
      statusText={statusText}
    />
  );
};

type StatusIndicatorProps = {
  statusText: string;
  icon: React.ReactNode;
};

const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  statusText,
  icon,
}) => {
  return (
    <m.div
      animate={{ opacity: 1 }}
      initial={{ opacity: 0 }}
      transition={{ duration: 0.15, delay: 0.1, ease: "easeOut" }}
    >
      <div className="flex items-center gap-1.5">
        {icon}
        <AnimatePresence mode="wait">
          <m.span
            key={statusText}
            animate={{ opacity: 1 }}
            className="font-inter-normal-12px text-gray-800"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: "easeInOut" }}
          >
            {statusText}
          </m.span>
        </AnimatePresence>
      </div>
    </m.div>
  );
};

const formatToolOutput = (content: unknown): string => {
  if (typeof content === "string") {
    try {
      const parsed = JSON.parse(content);
      return typeof parsed === "object"
        ? JSON.stringify(parsed, null, 2)
        : content;
    } catch {
      return content;
    }
  }
  return typeof content === "object"
    ? JSON.stringify(content, null, 2)
    : String(content);
};

const ToolCallBody: React.FC<{
  toolCall: ToolCallContentBlock;
  toolResult: ToolMessage | null;
}> = ({ toolCall, toolResult }) => {
  const isError = toolResult?.status === "error";

  const tabs = [
    {
      key: "input",
      label: "Input",
      content: (
        <CollapsiblePre title={`"${toolCall.metadata.name}" tool input`}>
          {JSON.stringify(toolCall.args, null, 2)}
        </CollapsiblePre>
      ),
    },
    {
      key: "output",
      label: "Output",
      disabled: !toolResult,
      content: toolResult ? (
        <CollapsiblePre title={`"${toolCall.metadata.name}" tool output`}>
          {formatToolOutput(toolResult.content)}
        </CollapsiblePre>
      ) : null,
    },
  ];

  return (
    <div className="flex flex-col gap-3 px-3 pb-3">
      <Divider />
      {toolCall.metadata.description && (
        <div>
          <span className="font-inter-medium-11px text-gray-500">
            Description
          </span>
          <p className="font-inter-normal-13px text-gray-800">
            {toolCall.metadata.description}
          </p>
        </div>
      )}
      {isError && (
        <Callout type="error">
          <span className="font-inter-normal-12px">
            {formatToolOutput(toolResult.content)}
          </span>
        </Callout>
      )}
      <Tabs
        defaultActiveKey="input"
        tabClassName="px-2"
        tabListClassName="border-b border-gray-200"
        tabs={tabs}
        unmountPanels={false}
      />
    </div>
  );
};

const ToolTypeIcon: React.FC<{ type: ToolType }> = ({ type }) => {
  const config = TOOL_ICON_CONFIG[type];
  if (!config) return null;
  return (
    <div
      className={twJoin(
        "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded",
        config.bgColor,
      )}
    >
      <Icon color={config.iconColor} icon={config.icon} size="2xs" />
    </div>
  );
};

const ToolCallStatusPill: React.FC<{ status: ToolCallStatus }> = ({
  status,
}) => {
  if (status === "success") {
    return (
      <Pill size="sm" variant="green">
        <Pill.Icon icon={faCheck} />
        <Pill.Text>Successful</Pill.Text>
      </Pill>
    );
  }
  if (status === "error") {
    return (
      <Pill size="sm" variant="red">
        <Pill.Icon icon={faWarning} />
        <Pill.Text>Failed</Pill.Text>
      </Pill>
    );
  }
  if (status === "running") {
    return (
      <Pill size="sm" variant="orange">
        <Pill.Icon icon={faCircleNotch} spin />
        <Pill.Text>Running</Pill.Text>
      </Pill>
    );
  }
  // paused
  return (
    <Tooltip
      body="Tool is awaiting response. This may take a while."
      placement="left"
      asChild
    >
      <div>
        <Pill size="sm" variant="yellow">
          <Pill.Icon icon={faCircleNotch} spin />
          <Pill.Text>Awaiting response</Pill.Text>
        </Pill>
      </div>
    </Tooltip>
  );
};

const AgentOutputStatusPill: React.FC<{ substatus: StoppedSubstatus }> = ({
  substatus,
}) => {
  if (substatus === "success") {
    return (
      <Pill size="sm" variant="green">
        <Pill.Icon icon={faCheck} />
        <Pill.Text>Successful</Pill.Text>
      </Pill>
    );
  }
  if (substatus === "failed") {
    return (
      <Pill size="sm" variant="red">
        <Pill.Icon icon={faExclamationCircle} />
        <Pill.Text>Failed</Pill.Text>
      </Pill>
    );
  }
  return (
    <Pill size="sm" variant="gray">
      <Pill.Icon icon={faStop} />
      <Pill.Text>Cancelled</Pill.Text>
    </Pill>
  );
};

const AgentOutputAccordionBody: React.FC<{
  result: SessionResultResponse;
}> = ({ result }) => {
  return (
    <div className="min-w-0 px-3 pb-3">
      {result.structured_output ? (
        <CollapsiblePre title="Agent Output">
          {JSON.stringify(result.structured_output, null, 2)}
        </CollapsiblePre>
      ) : (
        result.text && (
          <p className="whitespace-pre-wrap font-inter-normal-12px text-gray-700">
            {result.text}
          </p>
        )
      )}
    </div>
  );
};

const COLLAPSED_MAX_HEIGHT = 200;

type ToolCallContentWindowData = {
  title: string;
  content: string;
};

type CollapsiblePreProps = {
  children: string;
  title: string;
};

const CollapsiblePre: React.FC<CollapsiblePreProps> = ({ children, title }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const preRef = useRef<HTMLPreElement>(null);
  const { open: openWindow } = useFloatingWindowsActions();

  useEffect(() => {
    if (!preRef.current) return;

    const observer = new ResizeObserver(() => {
      if (!preRef.current || !preRef.current.checkVisibility()) return;
      setNeedsExpansion(preRef.current.scrollHeight > COLLAPSED_MAX_HEIGHT);
    });
    observer.observe(preRef.current);
    return () => observer.disconnect();
  }, []);

  const handleOpenFullscreen = () => {
    openWindow(
      {
        id: `tool-call-content-${uuidv4()}`,
        type: "tool-call-content",
        target: undefined,
      },
      { content: children, title } satisfies ToolCallContentWindowData,
    );
  };

  return (
    <div className="group/collapsible relative mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="absolute top-2 right-2 z-10 flex gap-1.5 opacity-0 transition-opacity group-hover/collapsible:opacity-100">
        <CopyTextIcon
          feedback="inline-only-checkmark"
          size="xs"
          tooltip="Copy"
          value={children}
        />
        <Tooltip placement="top" title="Expand" asChild>
          <Icon
            color="text-gray-500 hover:text-gray-800"
            icon={faExpand}
            size="xs"
            onClick={handleOpenFullscreen}
          />
        </Tooltip>
      </div>
      <pre
        ref={preRef}
        className={twJoin(
          "overflow-hidden whitespace-pre-wrap break-all font-mono text-gray-700 text-xs",
        )}
        style={{
          maxHeight: isExpanded ? undefined : COLLAPSED_MAX_HEIGHT,
        }}
      >
        {children}
      </pre>
      {needsExpansion && (
        <button
          className="mt-1 font-inter-medium-12px text-indigo-500 hover:text-indigo-600"
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? "See less" : "See more"}
        </button>
      )}
    </div>
  );
};

export const ToolCallContentWindow: React.FC<FloatingWindowProps> = ({
  computePosition,
  id,
  data,
}) => {
  const { close: closeWindow } = useFloatingWindowsActions();
  const { position, windowRef } = useFloatingWindowPosition(computePosition);

  const typedData = data as ToolCallContentWindowData;

  return (
    <FloatingWindowInternal
      ref={windowRef}
      height="h-120"
      id={id}
      style={{
        inset: "0px auto auto 0px",
        transform: position,
      }}
      title={typedData.title}
      width="w-160"
      draggable
      isMaximized
      isPinned
      maximizable
      resizeable
      onClose={() => closeWindow(id)}
    >
      <div className="h-full p-2">
        <CodeEditor
          language="json"
          value={typedData.content}
          disabled
          readOnly
        />
      </div>
    </FloatingWindowInternal>
  );
};
